import { createContext, useCallback, useContext, useEffect, useMemo, useState, type FC, type ReactNode } from 'react';

/**
 * O agente ao lado da tela: o painel lateral que a porta do cabeçalho abre.
 *
 * Mora no MainLayout. Guarda duas coisas: se o painel está aberto e em qual
 * registro a pessoa está — é isso que faz "ganhei essa" funcionar sem a
 * pergunta "qual oportunidade?". Quem diz o registro é a própria tela, com
 * `useRegistroDoAgente`; a API confere o id contra o alcance do perfil antes
 * de usar.
 */
export interface RegistroDoAgente {
    tipo: 'oportunidade';
    id: number;
    /** O que o topo do painel mostra ("Luiz Guilherme · Clínica Bem Estar"). */
    rotulo: string;
}

interface AgenteContexto {
    aberto: boolean;
    abrir: () => void;
    fechar: () => void;
    alternar: () => void;
    registro: RegistroDoAgente | null;
    definirRegistro: (r: RegistroDoAgente | null) => void;
}

const Contexto = createContext<AgenteContexto | null>(null);

export const AgenteProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [aberto, setAberto] = useState(false);
    const [registro, setRegistro] = useState<RegistroDoAgente | null>(null);

    const abrir = useCallback(() => setAberto(true), []);
    const fechar = useCallback(() => setAberto(false), []);
    const alternar = useCallback(() => setAberto(a => !a), []);

    const valor = useMemo(
        () => ({ aberto, abrir, fechar, alternar, registro, definirRegistro: setRegistro }),
        [aberto, abrir, fechar, alternar, registro],
    );

    return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
};

/** Nulo fora do MainLayout (telas públicas): a porta simplesmente não aparece. */
export const useAgente = () => useContext(Contexto);

/**
 * A tela diz em qual registro a pessoa está enquanto ela estiver montada.
 * Desmontou (saiu da ficha, fechou a gaveta), o registro volta a nulo.
 */
export const useRegistroDoAgente = (registro: RegistroDoAgente | null) => {
    const agente = useAgente();
    const definir = agente?.definirRegistro;
    const chave = registro ? `${registro.tipo}:${registro.id}:${registro.rotulo}` : '';

    useEffect(() => {
        if (!definir) return;
        definir(registro);
        return () => definir(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definir, chave]);
};
